import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import Select from "react-select";
import DropdownIndicator from "../../../inputs/DropdownIndicator";
import Button from "../../../buttons/Button";
import Spinner from "../../../utils/Spinner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash, faPlus } from "@fortawesome/pro-regular-svg-icons";
import Tag from "../../../utils/Tag";
import useApi from "../../../../hooks/useApi";
import {
  getCompanyDomains,
  getCompanySettings,
  addCompanyDomain,
  removeCompanyDomain,
  updateCompanyMembershipConfig,
} from "../../../../services/companyService";
import { extractDomain, isGenericDomain } from "../../../../utils/emailDomains";
import {
  buildDayOptions,
  buildMonthOptions,
  getDaysInMonth,
} from "../../../../utils/membershipRenewal";

const LIFETIME_OPTIONS = [3, 6, 9, 12];

const isFixedRenewalMode = (company) =>
  company?.membershipRenewalMonth != null &&
  company?.membershipRenewalDay != null &&
  Number(company.membershipRenewalMonth) >= 1 &&
  Number(company.membershipRenewalDay) >= 1;

const CompanyDomains = ({
  tKey = "dashboard.settings.domains.",
  data = null,
}) => {
  const { t, i18n } = useTranslation();
  const companyId = data?.companyId;
  const companyName = data?.name;

  const [domains, setDomains] = useState([]);
  const [newDomain, setNewDomain] = useState("");
  const [loading, setLoading] = useState(true);
  const [lifetimeMode, setLifetimeMode] = useState("months");
  const [lifetimeMonths, setLifetimeMonths] = useState(6);
  const [renewalMonth, setRenewalMonth] = useState(12);
  const [renewalDay, setRenewalDay] = useState(31);
  const [applyToAllMembers, setApplyToAllMembers] = useState(false);

  const { request: getDomainsRequest } = useApi(getCompanyDomains);
  const { request: getSettingsRequest } = useApi(getCompanySettings);
  const { request: addDomainRequest } = useApi(addCompanyDomain);
  const { request: removeDomainRequest } = useApi(removeCompanyDomain);
  const { request: updateConfigRequest } = useApi(
    updateCompanyMembershipConfig,
  );

  const applyMembershipConfig = useCallback((company) => {
    if (!company?.companyId) return;
    if (isFixedRenewalMode(company)) {
      setLifetimeMode("fixed");
      setRenewalMonth(Number(company.membershipRenewalMonth));
      setRenewalDay(Number(company.membershipRenewalDay));
    } else {
      setLifetimeMode("months");
      setLifetimeMonths(company.membershipLifetimeMonths ?? 6);
    }
    setApplyToAllMembers(false);
  }, []);

  const fetchMembershipConfig = useCallback(async () => {
    if (!companyId) return;
    try {
      const res = await getSettingsRequest(companyId);
      applyMembershipConfig(res.data);
    } catch (error) {
      console.error(error);
    }
  }, [companyId, getSettingsRequest, applyMembershipConfig]);

  const fetchDomains = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const res = await getDomainsRequest(companyId);
      setDomains(res.data || []);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [companyId, getDomainsRequest]);

  useEffect(() => {
    fetchDomains();
    fetchMembershipConfig();
  }, [fetchDomains, fetchMembershipConfig]);

  useEffect(() => {
    if (data?.companyId) {
      applyMembershipConfig(data);
    }
  }, [data, applyMembershipConfig]);

  const monthOptions = useMemo(
    () => buildMonthOptions(i18n.language),
    [i18n.language],
  );

  const dayOptions = useMemo(
    () => buildDayOptions(renewalMonth),
    [renewalMonth],
  );

  useEffect(() => {
    const maxDay = renewalMonth === 2 ? 28 : getDaysInMonth(renewalMonth);
    if (renewalDay > maxDay) {
      setRenewalDay(maxDay);
    }
  }, [renewalMonth, renewalDay]);

  const handleAddDomain = useCallback(async () => {
    const domain = extractDomain(newDomain);
    if (!domain) {
      toast.error(t(`${tKey}errors.invalid`));
      return;
    }
    if (isGenericDomain(domain)) {
      toast.error(t(`${tKey}errors.generic`));
      return;
    }
    if (domains.some((d) => d.domain === domain)) {
      toast.error(t(`${tKey}errors.duplicate`));
      return;
    }
    try {
      await addDomainRequest(companyId, domain);
      toast.success(t(`${tKey}add.success`, { domain }));
      setNewDomain("");
      await fetchDomains();
    } catch (error) {
      console.error(error);
      toast.error(error.message || t(`${tKey}errors.invalid`));
    }
  }, [newDomain, domains, addDomainRequest, companyId, fetchDomains, t, tKey]);

  const handleRemoveDomain = useCallback(
    async (domain) => {
      try {
        await removeDomainRequest(companyId, domain.companyDomainId);
        toast.success(t(`${tKey}remove.success`, { domain: domain.domain }));
        await fetchDomains();
      } catch (error) {
        console.error(error);
        toast.error(error.message);
      }
    },
    [removeDomainRequest, companyId, fetchDomains, t, tKey],
  );

  const handleSaveConfig = useCallback(async () => {
    const payload =
      lifetimeMode === "fixed"
        ? {
            membershipRenewalMonth: renewalMonth,
            membershipRenewalDay: renewalDay,
            membershipLifetimeMonths: null,
            ...(applyToAllMembers && { applyRenewalToAllMembers: true }),
          }
        : {
            membershipLifetimeMonths: lifetimeMonths,
            membershipRenewalMonth: null,
            membershipRenewalDay: null,
          };
    try {
      await updateConfigRequest(companyId, companyName, payload);
      toast.success(t(`${tKey}config.success`));
      await fetchMembershipConfig();
    } catch (error) {
      console.error(error);
      toast.error(error.message);
    }
  }, [
    lifetimeMode,
    renewalMonth,
    renewalDay,
    applyToAllMembers,
    lifetimeMonths,
    updateConfigRequest,
    companyId,
    companyName,
    fetchMembershipConfig,
    t,
    tKey,
  ]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddDomain();
    }
  };

  const lifetimeOptions = LIFETIME_OPTIONS.map((months) => ({
    label: t(`${tKey}config.months`, { count: months }),
    value: months,
  }));

  const selectProps = {
    components: { DropdownIndicator },
    className: "flex react-custom-select w-100 mb-3",
    classNamePrefix: "react-select",
    isClearable: false,
    isSearchable: false,
    menuPortalTarget: document.body,
    menuPosition: "fixed",
    styles: {
      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    },
  };

  if (loading) return <Spinner />;

  return (
    <div className="dashboard-content flex gap-2">
      <div className="w-50 bg-white rounded p-3">
        <h5 className="mt-0">{t(`${tKey}title`)}</h5>
        <p className="text-muted">{t(`${tKey}description`)}</p>
        <div className="flex gap-1 align-center mb-3">
          <input
            className="w-100 bg-pastel-light"
            type="text"
            value={newDomain}
            placeholder={t(`${tKey}add.placeholder`)}
            onChange={(e) => setNewDomain(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <Button variant="success" icon onClick={handleAddDomain}>
            <FontAwesomeIcon icon={faPlus} size="sm" />
          </Button>
        </div>
        {domains.length === 0 ? (
          <p className="text-muted">{t(`${tKey}empty`)}</p>
        ) : (
          <ul className="list-unstyled">
            {domains.map((domain, index) => (
              <li
                key={domain.companyDomainId}
                className="flex align-center justify-between p-2 border-bottom"
              >
                <div className="flex align-center gap-2">
                  <Tag variant="dark" round text={index + 1} />
                  <span>{domain.domain}</span>
                </div>
                <Button
                  variant="danger"
                  icon
                  onClick={() => handleRemoveDomain(domain)}
                >
                  <FontAwesomeIcon icon={faTrash} size="sm" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="w-50 bg-white rounded p-3">
        <h5 className="mt-0">{t(`${tKey}config.title`)}</h5>
        <p className="text-muted">{t(`${tKey}config.description`)}</p>

        <div className="ecosystem__toggle-wrapper">
          <div className="ecosystem__toggle">
            <button
              type="button"
              className={`ecosystem__toggle-btn ${
                lifetimeMode === "months" ? "ecosystem__toggle-btn--active" : ""
              }`}
              onClick={() => setLifetimeMode("months")}
            >
              {t(`${tKey}config.lifetimeLabel`)}
            </button>
            <button
              type="button"
              className={`ecosystem__toggle-btn ${
                lifetimeMode === "fixed" ? "ecosystem__toggle-btn--active" : ""
              }`}
              onClick={() => setLifetimeMode("fixed")}
            >
              {t(`${tKey}config.fixedLabel`)}
            </button>
          </div>
        </div>

        <div className="pb-3">
          {lifetimeMode === "months" ? (
            <Select
              {...selectProps}
              options={lifetimeOptions}
              value={
                lifetimeOptions.find((o) => o.value === lifetimeMonths) || null
              }
              getOptionLabel={(option) => option.label}
              getOptionValue={(option) => option.value}
              onChange={(option) => setLifetimeMonths(option.value)}
            />
          ) : (
            <div className="flex gap-1">
              <Select
                {...selectProps}
                options={dayOptions}
                value={dayOptions.find((o) => o.value === renewalDay) || null}
                getOptionLabel={(option) => option.label}
                getOptionValue={(option) => option.value}
                onChange={(option) => setRenewalDay(option.value)}
              />
              <Select
                {...selectProps}
                options={monthOptions}
                value={
                  monthOptions.find((o) => o.value === renewalMonth) || null
                }
                getOptionLabel={(option) => option.label}
                getOptionValue={(option) => option.value}
                onChange={(option) => setRenewalMonth(option.value)}
              />
            </div>
          )}
        </div>

        <Button variant="primary" onClick={handleSaveConfig}>
          {t(`${tKey}config.save`)}
        </Button>
      </div>
    </div>
  );
};

export default CompanyDomains;
